import React, { SetStateAction, useMemo, useState } from "react"
import { Button, Col, Flex, Form, Input, List, Radio, Row, Select, Tag, Tooltip, Typography } from "antd";

import { useForm } from "antd/es/form/Form";
import { EditOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { EmailService } from "../../Services/Contato/EmailService";
import { TypeEmail } from "../../Models/Pessoa/Contato/Email";

export const FormularioDeEmails: React.FC<{
    emailsAPI: [TypeEmail[], React.Dispatch<SetStateAction<TypeEmail[]>>],

    permitirEdicaoDosEmails?: boolean,
}> = (props) => {

    interface IDadosDoEmailPlanificado extends TypeEmail{
        codigoDoTipo?: number
    }

    const planificar = (formulario: TypeEmail): IDadosDoEmailPlanificado  => {
        return ({
            ...formulario,
            codigoDoTipo: formulario.tipo?.codigo
        })
    }

    const desplanificar = (formulario: IDadosDoEmailPlanificado): TypeEmail  => {
        const objetoCorrigido: TypeEmail = {
            ...formulario,
            tipo: {
                codigo: formulario.codigoDoTipo,
                tipo: tiposDeEmailDisponiveis.data?.data.find((t) => t.codigo === formulario.codigoDoTipo)?.tipo
            }
        }

        return objetoCorrigido;
    }

    const [emails, setEmails] = props.emailsAPI;
    const [ form ] = useForm<IDadosDoEmailPlanificado>()

    const [IDDoEmailEmEdicao, setIDDoEmailEmEdicao] = useState<number | null>()
    const [IDDeAdicao, setIDDeAdicao] = useState<number>(-1)
    
    const emailService = new EmailService()

    const tiposDeEmailDisponiveis = useQuery({
        queryKey: ["listarTipoEmails"],
        queryFn: () => emailService.listarTiposEmails()
    })

    const estaEditando = useMemo(() => {
        return IDDoEmailEmEdicao != null;
    }, [IDDoEmailEmEdicao, setIDDoEmailEmEdicao])

    const onFinish = (valores: IDadosDoEmailPlanificado) => {
        
        const objetoCorrigido = desplanificar(valores);

        objetoCorrigido.principal = emails.find((t) => t.codigo == IDDoEmailEmEdicao)?.principal
        objetoCorrigido.ativo = true;

        if (estaEditando) {
            objetoCorrigido.codigo = IDDoEmailEmEdicao!;
            setEmails((prev) => prev.map((t) => t.codigo == IDDoEmailEmEdicao ? objetoCorrigido : t))
            setIDDoEmailEmEdicao(null);
        } else {
            objetoCorrigido.codigo = IDDeAdicao;
            setIDDeAdicao(IDDeAdicao - 1)
            setEmails((prev) => [...prev, objetoCorrigido])
        }

        form.resetFields();
    }

    return <React.Fragment>

        <Flex vertical style={{width: '100%'}}>

            {IDDoEmailEmEdicao != null &&
                <Row gutter={16}>
                    <Col xs={24}>
                        {(() => {
                            const em = emails.find((v) => v.codigo == IDDoEmailEmEdicao)
                            if (em == null) return;
                            return <Typography.Paragraph>
                                <Tag color='geekblue-inverse'> <EditOutlined /> {em.endereco}</Tag>
                            </Typography.Paragraph>
                        })()}
                    </Col>
                </Row>
            }

            <Form form={form} onFinish={onFinish} autoComplete="off">
                <Row gutter={16}>
                    <Col xs={24} md={3}>
                        <Form.Item name="codigoDoTipo" rules={[{ required: true, message: 'Por favor, informe o tipo de e-mail!' }]}>
                            <Select 
                                id="email-codigo-tipo"
                                placeholder='Tipo'
                                loading={tiposDeEmailDisponiveis.isLoading && tiposDeEmailDisponiveis.isFetching}
                                disabled={tiposDeEmailDisponiveis.isError}
                                options={tiposDeEmailDisponiveis.data?.data.map((item) => {
                                    return {
                                        value: item.codigo,
                                        label: item.tipo
                                    }
                                })}

                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={5}>
                        <Form.Item name="nomeContato" rules={[{ required: true, message: 'Por favor, informe o nome do contato!' }]}>
                            <Input id="email-nome-contato" placeholder="Nome do Contato" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={6}>
                        <Form.Item name="endereco" rules={[{ required: true, type: "email", message: "Por favor, informe o e-mail do contato!" }]} >
                            <Input id="email-endereco" placeholder="E-mail" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={6}>
                        <Form.Item name="ocupacao" >
                            <Input id="email-ocupacao" placeholder="Ocupação" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={4} style={{ display: "flex", gap: "16px" }}>
                        {
                            <React.Fragment>
                                <Button type="primary" key="submit" htmlType='submit' style={{ flex: 1 }}>
                                    {estaEditando ? "Salvar" : "Incluir e-mail"}
                                </Button>

                                {estaEditando &&
                                    <Button type="default" danger style={{ flex: 1 }}
                                        onClick={() => {
                                            setIDDoEmailEmEdicao(null);
                                            form.resetFields();
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                }
                            </React.Fragment>
                        }

                    </Col>
                </Row>

            </Form>

            <Row>
                <Col xs={24}>
                    <div style={{ paddingTop: 16 }}>
                        <List
                            header={<div className="common-list-header">E-mails adicionados</div>}
                            bordered
                            locale={{ emptyText: 'Nenhum e-mail encontrado.' }}
                            dataSource={emails.filter((e) => e.ativo === true)}
                            renderItem={(item) =>
                                <List.Item
                                    className="common-list-item"
                                    actions={
                                        (() => {
                                            const editar = (
                                                <Button disabled={estaEditando} type='primary' size='small'
                                                    onClick={() => {
                                                        setIDDoEmailEmEdicao(item.codigo)
                                                        form.setFieldsValue(planificar(item))
                                                    }}>
                                                    Editar
                                                </Button>
                                             )

                                            const excluir = (
                                                <Tooltip title={item.principal ? "Não é possível excluir o e-mail principal." : null}>
                                                    <Button disabled={item.principal || estaEditando} type='default' danger size='small'
                                                        onClick={() => {
                                                            setEmails((prev) => prev.map((e) => e.codigo == item.codigo ? { ...e, ativo: false } : e))
                                                        }}>
                                                        Excluir
                                                    </Button>
                                                </Tooltip>
                                            )

                                            if(props.permitirEdicaoDosEmails){
                                                return [editar, excluir];
                                            }

                                            return [excluir];

                                        })()
                                        
                                    }>


                                    <Row className="common-list-item-render" gutter={8}>
                                        <Col>

                                            <span>
                                                <Radio checked={item.principal} onChange={() => {
                                                    setEmails((prev) => prev.map((em) => em.codigo == item.codigo ? { ...em, principal: true } : { ...em, principal: false }))
                                                }} />
                                            </span>

                                            {item.principal &&
                                                <Tag color='green'> Principal </Tag>
                                            }

                                            <Tag color='blue'> {item.tipo?.tipo} </Tag>

                                            {item.endereco}
                                        </Col>
                                    </Row>

                                </List.Item>
                            }
                        />
                    </div>
                </Col>
            </Row>

        </Flex>

    </React.Fragment>

}
